import { AdminMenuItemTypes } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /menu-item-types/* (системное
 * дерево типов меню-айтемов). Все ответы парсятся через Zod —
 * drift backend↔frontend падает громко.
 *
 * ⚠️ Caveat контракта: GET-query `isActive` — `z.coerce.boolean()`,
 * строка `'false'` коэрцится в `true` (JS `Boolean('false')`). Поэтому
 * параметр отправляется ТОЛЬКО при `isActive === true`, иначе опускается —
 * фильтр «только неактивные» через этот контракт невыразим.
 */
export const menuItemTypesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listAdminMenuItemTypes: build.query<
            AdminMenuItemTypes.GetAdminMenuItemTypesResponse,
            AdminMenuItemTypes.GetAdminMenuItemTypesRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.parentId !== undefined) {
                    search.parentId = params.parentId;
                }
                if (params?.isActive === true) {
                    search.isActive = 'true';
                }
                if (params?.q !== undefined && params.q !== '') {
                    search.q = params.q;
                }
                return {
                    url: '/menu-item-types',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (
                raw: unknown
            ): AdminMenuItemTypes.GetAdminMenuItemTypesResponse =>
                parseWithSchema(
                    AdminMenuItemTypes.GetAdminMenuItemTypesResponseSchema,
                    raw
                ),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((t) => ({
                            type: 'MenuItemType' as const,
                            id: t.id
                        })),
                        { type: 'MenuItemType' as const, id: 'LIST' }
                    ]
                    : [{ type: 'MenuItemType' as const, id: 'LIST' }]
        }),

        createAdminMenuItemType: build.mutation<
            AdminMenuItemTypes.CreateAdminMenuItemTypeResponse,
            AdminMenuItemTypes.CreateAdminMenuItemTypeRequest
        >({
            query: (body) => ({
                url: '/menu-item-types',
                method: 'POST',
                body
            }),
            transformResponse: (
                raw: unknown
            ): AdminMenuItemTypes.CreateAdminMenuItemTypeResponse =>
                parseWithSchema(
                    AdminMenuItemTypes.CreateAdminMenuItemTypeResponseSchema,
                    raw
                ),
            invalidatesTags: [{ type: 'MenuItemType', id: 'LIST' }]
        }),

        updateAdminMenuItemType: build.mutation<
            AdminMenuItemTypes.UpdateAdminMenuItemTypeResponse,
            AdminMenuItemTypes.UpdateAdminMenuItemTypeRequest & { id: string }
        >({
            // `:id` — только path-параметр (PATCH-body контракта `.strict()`
            // и НЕ содержит id — admin-backend валидирует его ParseUUIDPipe).
            query: ({ id, ...body }) => ({
                url: `/menu-item-types/${id}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (
                raw: unknown
            ): AdminMenuItemTypes.UpdateAdminMenuItemTypeResponse =>
                parseWithSchema(
                    AdminMenuItemTypes.UpdateAdminMenuItemTypeResponseSchema,
                    raw
                ),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'MenuItemType', id: arg.id },
                { type: 'MenuItemType', id: 'LIST' }
            ]
        }),

        deleteAdminMenuItemType: build.mutation<
            AdminMenuItemTypes.DeleteAdminMenuItemTypeResponse,
            AdminMenuItemTypes.DeleteAdminMenuItemTypeRequest
        >({
            query: ({ id }) => ({
                url: `/menu-item-types/${id}`,
                method: 'DELETE'
            }),
            // HTTP 204 — тела нет, парсить нечего (api-contracts.md).
            transformResponse: (): AdminMenuItemTypes.DeleteAdminMenuItemTypeResponse => ({}),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'MenuItemType', id: arg.id },
                { type: 'MenuItemType', id: 'LIST' }
            ]
        }),

        reorderAdminMenuItemTypes: build.mutation<
            AdminMenuItemTypes.ReorderAdminMenuItemTypesResponse,
            AdminMenuItemTypes.ReorderAdminMenuItemTypesRequest
        >({
            query: (body) => ({
                url: '/menu-item-types/reorder',
                method: 'POST',
                body
            }),
            transformResponse: (
                raw: unknown
            ): AdminMenuItemTypes.ReorderAdminMenuItemTypesResponse =>
                parseWithSchema(
                    AdminMenuItemTypes.ReorderAdminMenuItemTypesResponseSchema,
                    raw
                ),
            invalidatesTags: [{ type: 'MenuItemType', id: 'LIST' }]
        })
    })
});

export const {
    useListAdminMenuItemTypesQuery,
    useCreateAdminMenuItemTypeMutation,
    useUpdateAdminMenuItemTypeMutation,
    useDeleteAdminMenuItemTypeMutation,
    useReorderAdminMenuItemTypesMutation
} = menuItemTypesApi;
